import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  Divider,
  Grid,
  Switch,
  FormControlLabel,
  CircularProgress,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { municipiosService, saudeService, monitoramentoService } from '../services/api';

interface Municipio {
  id: number;
  codigo_ibge: string;
  nome: string;
  ativo: boolean;
  // Parametrizacao do CVAT (NT 30/2025). Ficam nulos ate serem preenchidos aqui.
  porte_populacional?: number | null;
  populacao_ibge?: number | null;
}

/**
 * Porte populacional do Anexo XCIX da Portaria de Consolidacao GM/MS 6/2017.
 * Define a "populacao parametro" de cada equipe, que e o DENOMINADOR das duas
 * dimensoes do CVAT. Nao existe no banco do PEC - e dado do municipio.
 */
const PORTES_POPULACIONAIS = [
  { valor: 1, label: '1 - Ate 20 mil habitantes', parametros: 'eSF 2.000 | eAP 30h 1.500 | eAP 20h 1.000' },
  { valor: 2, label: '2 - Acima de 20 mil ate 50 mil', parametros: 'eSF 2.500 | eAP 30h 1.875 | eAP 20h 1.250' },
  { valor: 3, label: '3 - Acima de 50 mil ate 100 mil', parametros: 'eSF 2.750 | eAP 30h 2.063 | eAP 20h 1.375' },
  { valor: 4, label: '4 - Acima de 100 mil', parametros: 'eSF 3.000 | eAP 30h 2.250 | eAP 20h 1.500' },
];

interface Conexao {
  id: number;
  municipio_id: number;
  nome: string;
  host: string;
  porta: number;
  banco: string;
  usuario: string;
  esquema?: string;
  ssl: boolean;
  ativo: boolean;
  usa_dwh?: boolean;
  usa_snapshot?: boolean;
  modo_pec?: 'direto' | 'xml';
  ultimo_teste_ok: boolean | null;
  ultimo_teste_em: string | null;
}

const ESUS_FORM_INITIAL = {
  host: '',
  porta: 5432,
  banco: 'esus',
  usuario: '',
  senha: '',
  ssl: false,
  usa_dwh: false,
  usa_snapshot: false,
  modo_pec: 'direto' as 'direto' | 'xml',
};

const DW_FORM_INITIAL = {
  host: '',
  porta: 5432,
  banco: 'estatisticas_db',
  esquema: '',
  usuario: '',
  senha: '',
  ssl: false,
};

const MunicipiosPage: React.FC = () => {
  const [municipios, setMunicipios] = useState<Municipio[]>([]);
  const [conexoesMap, setConexoesMap] = useState<Map<number, Conexao>>(new Map());
  const [conexoesDwMap, setConexoesDwMap] = useState<Map<number, Conexao>>(new Map());
  const [openDialog, setOpenDialog] = useState(false);
  const [editingMunicipio, setEditingMunicipio] = useState<Municipio | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    codigo_ibge: '',
    nome: '',
    porte_populacional: '' as string | number,
    populacao_ibge: '' as string | number,
  });
  const [esusForm, setEsusForm] = useState(ESUS_FORM_INITIAL);
  const [conexaoExistente, setConexaoExistente] = useState<Conexao | null>(null);
  const [dwForm, setDwForm] = useState(DW_FORM_INITIAL);
  const [conexaoDwExistente, setConexaoDwExistente] = useState<Conexao | null>(null);

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string; latency?: number } | null>(null);
  const [testingDw, setTestingDw] = useState(false);
  const [testResultDw, setTestResultDw] = useState<{ ok: boolean; message: string; latency?: number } | null>(null);

  const loadConexoes = useCallback(async () => {
    try {
      const conexoes: Conexao[] = await saudeService.getConexoes(0);
      const map = new Map<number, Conexao>();
      for (const c of conexoes) {
        map.set(c.municipio_id, c);
      }
      setConexoesMap(map);
    } catch {
      // Silently fail - user may not have permission
    }
    try {
      const conexoesDw: Conexao[] = await monitoramentoService.getConexoesDw();
      const mapDw = new Map<number, Conexao>();
      for (const c of conexoesDw) {
        mapDw.set(c.municipio_id, c);
      }
      setConexoesDwMap(mapDw);
    } catch {
      // Silently fail
    }
  }, []);

  const loadMunicipios = useCallback(async () => {
    try {
      const data = await municipiosService.getMunicipios();
      setMunicipios(data);
    } catch {
      setError('Erro ao carregar municípios');
    }
  }, []);

  useEffect(() => {
    loadMunicipios();
    loadConexoes();
  }, [loadMunicipios, loadConexoes]);

  const handleOpenDialog = (municipio?: Municipio) => {
    setTestResult(null);
    setTestResultDw(null);
    if (municipio) {
      setEditingMunicipio(municipio);
      setFormData({
        codigo_ibge: municipio.codigo_ibge,
        nome: municipio.nome,
        porte_populacional: municipio.porte_populacional ?? '',
        populacao_ibge: municipio.populacao_ibge ?? '',
      });
      // e-SUS
      const conexao = conexoesMap.get(municipio.id) || null;
      setConexaoExistente(conexao);
      if (conexao) {
        setEsusForm({
          host: conexao.host,
          porta: conexao.porta,
          banco: conexao.banco,
          usuario: conexao.usuario,
          senha: '',
          ssl: conexao.ssl,
          usa_dwh: conexao.usa_dwh || false,
          usa_snapshot: conexao.usa_snapshot || false,
          modo_pec: conexao.modo_pec || 'direto',
        });
      } else {
        setEsusForm(ESUS_FORM_INITIAL);
      }
      // DW
      const conexaoDw = conexoesDwMap.get(municipio.id) || null;
      setConexaoDwExistente(conexaoDw);
      if (conexaoDw) {
        setDwForm({
          host: conexaoDw.host,
          porta: conexaoDw.porta,
          banco: conexaoDw.banco,
          esquema: conexaoDw.esquema || '',
          usuario: conexaoDw.usuario,
          senha: '',
          ssl: conexaoDw.ssl,
        });
      } else {
        setDwForm(DW_FORM_INITIAL);
      }
    } else {
      setEditingMunicipio(null);
      setConexaoExistente(null);
      setConexaoDwExistente(null);
      setFormData({ codigo_ibge: '', nome: '', porte_populacional: '', populacao_ibge: '' });
      setEsusForm(ESUS_FORM_INITIAL);
      setDwForm(DW_FORM_INITIAL);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingMunicipio(null);
    setConexaoExistente(null);
    setConexaoDwExistente(null);
    setTestResult(null);
    setTestResultDw(null);
  };

  const handleSave = async () => {
    try {
      let municipioId: number;

      if (editingMunicipio) {
        await municipiosService.updateMunicipio(editingMunicipio.id, formData);
        municipioId = editingMunicipio.id;
      } else {
        const created = await municipiosService.createMunicipio(formData);
        municipioId = created.id;
      }

      // Save eSUS connection if host is filled
      if (esusForm.host && esusForm.usuario) {
        try {
          if (conexaoExistente) {
            const updateData: any = {
              nome: `e-SUS ${formData.nome}`,
              host: esusForm.host,
              porta: esusForm.porta,
              banco: esusForm.banco,
              usuario: esusForm.usuario,
              ssl: esusForm.ssl,
              usa_dwh: esusForm.usa_dwh,
              usa_snapshot: esusForm.usa_snapshot,
              modo_pec: esusForm.modo_pec,
            };
            if (esusForm.senha) updateData.senha = esusForm.senha;
            await saudeService.updateConexao(conexaoExistente.id, updateData);
          } else {
            await saudeService.createConexao({
              municipio_id: municipioId,
              nome: `e-SUS ${formData.nome}`,
              host: esusForm.host,
              porta: esusForm.porta,
              banco: esusForm.banco,
              usuario: esusForm.usuario,
              senha: esusForm.senha,
              ssl: esusForm.ssl,
              usa_dwh: esusForm.usa_dwh,
              usa_snapshot: esusForm.usa_snapshot,
              modo_pec: esusForm.modo_pec,
            });
          }
        } catch (err: any) {
          setError(err.response?.data?.message || 'Município salvo, mas erro ao salvar conexão eSUS');
          handleCloseDialog();
          loadMunicipios();
          loadConexoes();
          return;
        }
      }

      // Save DW connection if host is filled
      if (dwForm.host && dwForm.usuario) {
        try {
          if (conexaoDwExistente) {
            const updateData: any = {
              nome: `DW ${formData.nome}`,
              host: dwForm.host,
              porta: dwForm.porta,
              banco: dwForm.banco,
              esquema: dwForm.esquema,
              usuario: dwForm.usuario,
              ssl: dwForm.ssl,
            };
            if (dwForm.senha) updateData.senha = dwForm.senha;
            await monitoramentoService.updateConexaoDw(conexaoDwExistente.id, updateData);
          } else {
            await monitoramentoService.createConexaoDw({
              municipio_id: municipioId,
              nome: `DW ${formData.nome}`,
              host: dwForm.host,
              porta: dwForm.porta,
              banco: dwForm.banco,
              esquema: dwForm.esquema,
              usuario: dwForm.usuario,
              senha: dwForm.senha,
              ssl: dwForm.ssl,
            });
          }
        } catch (err: any) {
          setError(err.response?.data?.message || 'Município salvo, mas erro ao salvar conexão DW');
          handleCloseDialog();
          loadMunicipios();
          loadConexoes();
          return;
        }
      }

      setSuccess(editingMunicipio ? 'Município atualizado com sucesso!' : 'Município criado com sucesso!');
      handleCloseDialog();
      loadMunicipios();
      loadConexoes();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Erro ao salvar município');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir este município?')) return;
    try {
      await municipiosService.deleteMunicipio(id);
      setSuccess('Município excluído com sucesso!');
      loadMunicipios();
      loadConexoes();
      setTimeout(() => setSuccess(''), 3000);
    } catch {
      setError('Erro ao excluir município');
    }
  };

  const handleToggleStatus = async (municipio: Municipio) => {
    try {
      await municipiosService.updateMunicipio(municipio.id, { ativo: !municipio.ativo });
      setSuccess('Status atualizado com sucesso!');
      loadMunicipios();
      setTimeout(() => setSuccess(''), 3000);
    } catch {
      setError('Erro ao atualizar status');
    }
  };

  const handleTestConexao = async () => {
    if (!conexaoExistente) return;
    setTesting(true);
    setTestResult(null);
    try {
      const result = await saudeService.testarConexao(conexaoExistente.id);
      setTestResult({
        ok: result.ok,
        message: result.ok ? 'Conexão bem-sucedida!' : result.error || 'Falha na conexão',
        latency: result.latency,
      });
      loadConexoes();
    } catch (err: any) {
      setTestResult({
        ok: false,
        message: err.response?.data?.message || 'Erro ao testar conexão',
      });
    } finally {
      setTesting(false);
    }
  };

  const handleTestConexaoDw = async () => {
    if (!conexaoDwExistente) return;
    setTestingDw(true);
    setTestResultDw(null);
    try {
      const result = await monitoramentoService.testarConexaoDw(conexaoDwExistente.id);
      setTestResultDw({
        ok: result.ok,
        message: result.ok ? 'Conexão DW bem-sucedida!' : result.error || 'Falha na conexão DW',
        latency: result.latency,
      });
      loadConexoes();
    } catch (err: any) {
      setTestResultDw({
        ok: false,
        message: err.response?.data?.message || 'Erro ao testar conexão DW',
      });
    } finally {
      setTestingDw(false);
    }
  };

  const renderConexaoStatus = (municipioId: number) => {
    const conexao = conexoesMap.get(municipioId);
    if (!conexao) return <Chip label="Não configurado" size="small" variant="outlined" />;
    if (conexao.ultimo_teste_ok === null) return <Chip label="Não testado" size="small" variant="outlined" />;
    if (conexao.ultimo_teste_ok) return <Chip icon={<CheckCircle />} label={conexao.usa_dwh ? 'DWH' : 'PEC'} size="small" color="success" />;
    return <Chip icon={<Cancel />} label="Falha" size="small" color="error" />;
  };

  const renderConexaoDwStatus = (municipioId: number) => {
    const conexao = conexoesDwMap.get(municipioId);
    if (!conexao) return <Chip label="Não configurado" size="small" variant="outlined" />;
    if (conexao.ultimo_teste_ok === null) return <Chip label="Não testado" size="small" variant="outlined" />;
    if (conexao.ultimo_teste_ok) return <Chip icon={<CheckCircle />} label="Conectado" size="small" color="success" />;
    return <Chip icon={<Cancel />} label="Falha" size="small" color="error" />;
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">Gerenciar Municípios</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          Novo Município
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Código IBGE</TableCell>
              <TableCell>Nome</TableCell>
              <TableCell>Porte</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>e-SUS</TableCell>
              <TableCell>DW</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {municipios.map((municipio) => (
              <TableRow key={municipio.id}>
                <TableCell>{municipio.codigo_ibge}</TableCell>
                <TableCell>{municipio.nome}</TableCell>
                <TableCell>
                  {municipio.porte_populacional ? (
                    <Chip label={`Porte ${municipio.porte_populacional}`} size="small" variant="outlined" />
                  ) : (
                    <Chip label="Não definido" size="small" color="warning" variant="outlined" />
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={municipio.ativo ? 'Ativo' : 'Inativo'}
                    size="small"
                    color={municipio.ativo ? 'success' : 'default'}
                    onClick={() => handleToggleStatus(municipio)}
                    sx={{ cursor: 'pointer' }}
                  />
                </TableCell>
                <TableCell>{renderConexaoStatus(municipio.id)}</TableCell>
                <TableCell>{renderConexaoDwStatus(municipio.id)}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" color="primary" onClick={() => handleOpenDialog(municipio)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(municipio.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingMunicipio ? 'Editar Município' : 'Novo Município'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Código IBGE"
              fullWidth
              value={formData.codigo_ibge}
              onChange={(e) => setFormData({ ...formData, codigo_ibge: e.target.value })}
              inputProps={{ maxLength: 7 }}
              helperText="7 dígitos do código IBGE do município"
            />
            <TextField
              label="Nome"
              fullWidth
              value={formData.nome}
              onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
            />

            {/*
              Porte populacional: define a populacao parametro de cada equipe, que e o
              DENOMINADOR das duas dimensoes do CVAT (NT 30/2025, Anexo XCIX). Nao existe
              no banco do PEC. Sem ele, o painel /saude/cvat fica bloqueado.
              Atencao: o porte afeta TODAS as equipes do municipio de uma vez.
            */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={7}>
                <TextField
                  select
                  label="Porte Populacional"
                  fullWidth
                  value={formData.porte_populacional}
                  onChange={(e) => setFormData({ ...formData, porte_populacional: e.target.value })}
                  helperText={
                    formData.porte_populacional
                      ? PORTES_POPULACIONAIS.find((p) => p.valor === Number(formData.porte_populacional))?.parametros
                      : 'Necessário para o cálculo do CVAT (Vínculo e Acompanhamento Territorial)'
                  }
                >
                  <MenuItem value="">
                    <em>Não definido</em>
                  </MenuItem>
                  {PORTES_POPULACIONAIS.map((p) => (
                    <MenuItem key={p.valor} value={p.valor}>
                      {p.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField
                  label="População (IBGE)"
                  fullWidth
                  type="number"
                  value={formData.populacao_ibge}
                  onChange={(e) => setFormData({ ...formData, populacao_ibge: e.target.value })}
                  inputProps={{ min: 0 }}
                  helperText="Opcional — só usada em municípios muito pequenos"
                />
              </Grid>
            </Grid>

            {/* ===== Conexão e-SUS PEC ===== */}
            <Divider sx={{ my: 1 }}>
              <Chip
                label={esusForm.usa_dwh ? 'Conexão e-SUS (via Data Warehouse)' : 'Conexão e-SUS PEC'}
                variant="outlined"
                size="small"
                color={esusForm.usa_dwh ? 'info' : 'default'}
              />
            </Divider>

            <Typography variant="body2" color="text.secondary" sx={{ mb: -1 }}>
              {esusForm.usa_dwh
                ? 'Esta conexão aponta para um Data Warehouse. As queries de indicadores usarão o formato DWH.'
                : 'Configure a conexão com o banco de dados e-SUS PEC deste município para consultar indicadores de saúde.'}
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={esusForm.usa_dwh}
                  onChange={(e) => setEsusForm({ ...esusForm, usa_dwh: e.target.checked })}
                  color="info"
                />
              }
              label="Conectar via Data Warehouse"
            />

            {!esusForm.usa_dwh && (
              <TextField
                select
                label="Origem dos cadastros"
                fullWidth
                value={esusForm.modo_pec}
                onChange={(e) => setEsusForm({ ...esusForm, modo_pec: e.target.value as 'direto' | 'xml' })}
                helperText={
                  esusForm.modo_pec === 'xml'
                    ? 'Os cadastros vêm de outro sistema por XML — as consultas usam a ficha de cadastro individual como fonte de nascimento, equipe, unidade e microárea.'
                    : 'Os cadastros são feitos dentro do PEC — as consultas usam as tabelas de cidadão do próprio PEC.'
                }
              >
                <MenuItem value="direto">Cadastro feito no PEC</MenuItem>
                <MenuItem value="xml">Cadastro importado por XML</MenuItem>
              </TextField>
            )}

            {/* Vale para as duas origens de cadastro. O perfil XML ficou de fora até 13/09/2026
                porque a busca dos dados do cidadão para montar a lista não era por chave ali; hoje
                é, nos dois perfis. Segue indisponível para conexão via Data Warehouse. */}
            {!esusForm.usa_dwh && (
              <FormControlLabel
                control={
                  <Switch
                    checked={esusForm.usa_snapshot}
                    onChange={(e) => setEsusForm({ ...esusForm, usa_snapshot: e.target.checked })}
                    color="primary"
                  />
                }
                label="Consolidação noturna (dados de ontem)"
                sx={{ mt: -1 }}
              />
            )}
            {!esusForm.usa_dwh && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: -2, ml: 6 }}>
                Os indicadores passam a ser consolidados de madrugada e servidos sem consultar o
                prontuário, com opção de ver em tempo real na própria tela. Recomendado em
                municípios grandes, onde a consulta ao vivo concorre com os atendimentos.
              </Typography>
            )}

            <Grid container spacing={2}>
              <Grid item xs={8}>
                <TextField
                  label="Host"
                  fullWidth
                  value={esusForm.host}
                  onChange={(e) => setEsusForm({ ...esusForm, host: e.target.value })}
                  placeholder="Ex: 192.168.1.100"
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Porta"
                  fullWidth
                  type="number"
                  value={esusForm.porta}
                  onChange={(e) => setEsusForm({ ...esusForm, porta: parseInt(e.target.value, 10) || 5432 })}
                />
              </Grid>
            </Grid>
            <TextField
              label="Banco de Dados"
              fullWidth
              value={esusForm.banco}
              onChange={(e) => setEsusForm({ ...esusForm, banco: e.target.value })}
              placeholder="Ex: esus"
            />
            <TextField
              label="Usuário"
              fullWidth
              value={esusForm.usuario}
              onChange={(e) => setEsusForm({ ...esusForm, usuario: e.target.value })}
              placeholder="Ex: postgres"
            />
            <TextField
              label={conexaoExistente ? 'Senha (deixe em branco para manter)' : 'Senha'}
              fullWidth
              type="password"
              value={esusForm.senha}
              onChange={(e) => setEsusForm({ ...esusForm, senha: e.target.value })}
              helperText={conexaoExistente ? 'Deixe em branco para manter a senha atual' : undefined}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={esusForm.ssl}
                  onChange={(e) => setEsusForm({ ...esusForm, ssl: e.target.checked })}
                />
              }
              label="Conexão SSL"
            />

            {conexaoExistente && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={testing ? <CircularProgress size={16} /> : <PlayArrow />}
                  onClick={handleTestConexao}
                  disabled={testing}
                >
                  Testar Conexão
                </Button>
                {conexaoExistente.ultimo_teste_ok === true && (
                  <Chip icon={<CheckCircle />} label="Conectado" size="small" color="success" />
                )}
                {conexaoExistente.ultimo_teste_ok === false && (
                  <Chip icon={<Cancel />} label="Falha" size="small" color="error" />
                )}
              </Box>
            )}

            {testResult && (
              <Alert severity={testResult.ok ? 'success' : 'error'} sx={{ mt: -1 }}>
                {testResult.message}
                {testResult.latency != null && (
                  <Typography component="span" variant="body2" sx={{ ml: 1 }}>
                    ({testResult.latency}ms)
                  </Typography>
                )}
              </Alert>
            )}

            {/* ===== Conexão Data Warehouse ===== */}
            <Divider sx={{ my: 1 }}>
              <Chip label="Conexão Data Warehouse (Monitoramento)" variant="outlined" size="small" />
            </Divider>

            <Typography variant="body2" color="text.secondary" sx={{ mb: -1 }}>
              Configure a conexão com o banco de dados DW (estatísticas) deste município para os painéis de monitoramento.
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={8}>
                <TextField
                  label="Host"
                  fullWidth
                  value={dwForm.host}
                  onChange={(e) => setDwForm({ ...dwForm, host: e.target.value })}
                  placeholder="Ex: 192.168.1.100"
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Porta"
                  fullWidth
                  type="number"
                  value={dwForm.porta}
                  onChange={(e) => setDwForm({ ...dwForm, porta: parseInt(e.target.value, 10) || 5432 })}
                />
              </Grid>
            </Grid>
            <TextField
              label="Banco de Dados"
              fullWidth
              value={dwForm.banco}
              onChange={(e) => setDwForm({ ...dwForm, banco: e.target.value })}
              placeholder="Ex: estatisticas_db"
            />
            <TextField
              label="Esquema"
              fullWidth
              value={dwForm.esquema}
              onChange={(e) => setDwForm({ ...dwForm, esquema: e.target.value })}
              placeholder="Ex: sc_estatisticas"
              helperText="Opcional. Vazio = schema padrão (public)."
            />
            <TextField
              label="Usuário"
              fullWidth
              value={dwForm.usuario}
              onChange={(e) => setDwForm({ ...dwForm, usuario: e.target.value })}
              placeholder="Ex: postgres"
            />
            <TextField
              label={conexaoDwExistente ? 'Senha (deixe em branco para manter)' : 'Senha'}
              fullWidth
              type="password"
              value={dwForm.senha}
              onChange={(e) => setDwForm({ ...dwForm, senha: e.target.value })}
              helperText={conexaoDwExistente ? 'Deixe em branco para manter a senha atual' : undefined}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={dwForm.ssl}
                  onChange={(e) => setDwForm({ ...dwForm, ssl: e.target.checked })}
                />
              }
              label="Conexão SSL"
            />

            {conexaoDwExistente && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={testingDw ? <CircularProgress size={16} /> : <PlayArrow />}
                  onClick={handleTestConexaoDw}
                  disabled={testingDw}
                >
                  Testar Conexão DW
                </Button>
                {conexaoDwExistente.ultimo_teste_ok === true && (
                  <Chip icon={<CheckCircle />} label="Conectado" size="small" color="success" />
                )}
                {conexaoDwExistente.ultimo_teste_ok === false && (
                  <Chip icon={<Cancel />} label="Falha" size="small" color="error" />
                )}
              </Box>
            )}

            {testResultDw && (
              <Alert severity={testResultDw.ok ? 'success' : 'error'} sx={{ mt: -1 }}>
                {testResultDw.message}
                {testResultDw.latency != null && (
                  <Typography component="span" variant="body2" sx={{ ml: 1 }}>
                    ({testResultDw.latency}ms)
                  </Typography>
                )}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained">Salvar</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MunicipiosPage;
